import { useTranslation } from "react-i18next";

import { LEGAL_SLUGS, useProfile } from "@/entities/profile";
import { ROUTES } from "@/shared/config";
import { openSupportLink } from "@/shared/telegram";
import { Button, MenuRow } from "@/shared/ui";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Помощь и документы. Figma: `Profile · Support` (27:151) плюс список из
 * `Legal & Risk` (27:193).
 *
 * В макете правовых документов шесть, API обслуживает три слага и на любой
 * другой отвечает 404 — список строится из белого списка, а не из макета
 * (DESIGN.md §8). Ссылка поддержки приходит в `GET /profile`.
 */
export function SupportPage() {
  const { t } = useTranslation();
  const { data, isPending, isError, refetch } = useProfile();

  if (isPending) return <ScreenSkeleton blocks={[96, 72, 72, 72]} />;

  if (isError) {
    return (
      <ScreenState
        tone="loss"
        icon="warningFill"
        title={t("states.error.title")}
        body={t("states.error.body")}
        action={{ label: t("states.error.action"), onClick: () => void refetch() }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("profile.menu.support")} subtitle={t("profile.support.subtitle")} />

      <section className="flex flex-col gap-3 rounded-3xl border border-border-subtle bg-background-elevated p-5">
        <p className="text-caption font-normal text-text-secondary">{t("profile.support.body")}</p>
        <Button
          size="cta"
          block
          onClick={() => {
            // Вне Telegram методы клиента недоступны — тогда открываем
            // обычной навигацией, иначе кнопка молча ничего не делала бы.
            if (!openSupportLink(data.support_link)) window.open(data.support_link, "_blank");
          }}
        >
          {t("profile.support.action")}
        </Button>
      </section>

      <h2 className="px-1 pt-2 text-label font-semibold text-text-secondary">
        {t("profile.legal.title")}
      </h2>

      <nav aria-label={t("profile.legal.title")} className="stagger-children flex flex-col gap-3">
        {LEGAL_SLUGS.map((slug) => (
          <MenuRow key={slug} to={ROUTES.legal(slug)} icon="scroll" title={t(`profile.legal.${slug}`)} />
        ))}
      </nav>
    </div>
  );
}
